import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImageUpload } from "@/components/ImageUpload";

const PRIVACIES = ["public", "link_only", "friends", "private"];

const empty = { title: "", description: "", cover_image: "", event_date: "", privacy: "public" };

export const WishlistDialog = ({ open, onOpenChange, editWishlist, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(editWishlist ? {
      title: editWishlist.title, description: editWishlist.description || "",
      cover_image: editWishlist.cover_image || "",
      event_date: editWishlist.event_date ? editWishlist.event_date.slice(0, 10) : "",
      privacy: editWishlist.privacy,
    } : empty);
  }, [open, editWishlist]);

  const save = async () => {
    if (!form.title.trim()) { toast.error(t("list_name_req")); return; }
    setBusy(true);
    const payload = { ...form, event_date: form.event_date || null };
    try {
      if (editWishlist) {
        await api.put(`/wishlists/${editWishlist.wishlist_id}`, payload);
        toast.success(t("list_updated"));
      } else {
        await api.post("/wishlists", payload);
        toast.success(t("list_created"));
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{editWishlist ? t("edit_list_t") : t("new_list_t")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t("lbl_list_name")}</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 rounded-xl" placeholder={t("list_name_ph")} data-testid="wishlist-title-input" />
            <p className="text-[11px] text-muted-foreground mt-2 mb-1">{t("quick_names")}:</p>
            <div className="flex flex-wrap gap-1.5">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <button key={n} type="button" onClick={() => setForm((f) => ({ ...f, title: t(`preset_${n}`) }))}
                  data-testid={`wishlist-preset-${n}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    form.title === t(`preset_${n}`) ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                  {t(`preset_${n}`)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>{t("lbl_desc")}</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 rounded-xl" rows={2} placeholder={t("desc_ph")} data-testid="wishlist-desc-input" />
          </div>
          <div>
            <Label>{t("lbl_cover")}</Label>
            {form.cover_image && <img src={form.cover_image} alt="" className="w-full h-28 object-cover rounded-xl mt-1 mb-2" />}
            <div className="flex gap-2 mt-1 items-center">
              <ImageUpload aspect={16 / 9} label={t("upload")} testId="wishlist-cover-upload"
                onUploaded={(url) => setForm((f) => ({ ...f, cover_image: url }))} />
              <Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                className="rounded-xl flex-1" placeholder={t("or_img_url")} data-testid="wishlist-cover-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("lbl_event_date")}</Label>
              <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className="mt-1 rounded-xl" data-testid="wishlist-date-input" />
            </div>
            <div>
              <Label>{t("lbl_privacy")}</Label>
              <Select value={form.privacy} onValueChange={(v) => setForm({ ...form, privacy: v })}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="wishlist-privacy-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIVACIES.map((k) => <SelectItem key={k} value={k}>{t(`priv_${k}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={save} disabled={busy} className="w-full rounded-full h-11 font-semibold" data-testid="wishlist-save-btn">
            {busy ? t("saving") : editWishlist ? t("update_btn") : t("create_list_btn")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
